import { useRef } from 'react';

import { If, createComponent, IntrinsicProps } from '@common/util/templateHelpers';
import Field, { FieldHelp } from '@common/components/controls/Field';
import Control from '@common/components/controls/Control';
import { useLoadScripts } from '@common/hooks/useLoadScripts';
import { UseForm } from '../../hooks/useForm';
import { config } from '@common/config';

export enum RecaptchaTheme {
  LIGHT = 'light',
  DARK = 'dark'
}

/* --- */

interface RecaptchaProps extends IntrinsicProps {
  theme?: RecaptchaTheme,
  onSuccess?: (token: string) => void,
  onError?: (err: string) => void,
  siteKey: string
}

const Recaptcha = createComponent<RecaptchaProps>('Recaptcha', {}, function Recaptcha ({ className, style }, props) {
  const theme = props.theme || RecaptchaTheme.LIGHT;
  const recaptcha = useRef(null);

  const verify = (responseToken) => {
    if (props.onSuccess) props.onSuccess(responseToken);
  };

  const error = (err: string) => {
    if (props.onError) props.onError(err);
  };

  const render = () => {
    recaptcha.current.render('recaptchaEl', {
      sitekey: props.siteKey,
      theme,
      callback: verify,
      'error-callback': error
    });
  };

  if (typeof window !== undefined) {
    // @ts-ignore
    window.recaptchaLoaded = () => {
      // @ts-ignore
      recaptcha.current = window.grecaptcha;
      render();
    };
  }
  useLoadScripts([ 'https://www.google.com/recaptcha/api.js?onload=recaptchaLoaded&render=explicit' ]);

  return (
    <div className={className} style={style} id='recaptchaEl' />
  );
});
export default Recaptcha;

/* --- */

interface RecaptchaFieldProps extends RecaptchaProps {
  name: string,
  form: UseForm
}

export const RecaptchaField = createComponent<RecaptchaFieldProps>('RecaptchaField', {}, function RecaptchaField ({ className, style, slots }, props) {
  const key = props.name;
  const form = props.form;

  const success = (token) => {
    form.validateField(key, token);
  };

  const error = (err) => {
    // eslint-disable-next-line no-console
    if (config.NODE_ENV === 'development') console.warn('Recaptcha error', err);
    form.validateField(key, false);
  };

  return (
    <Field key={`field_${key}`} className={className} style={style}>
      <Control>
        <Recaptcha
          theme={props.theme}
          siteKey={props.siteKey}
          onSuccess={success}
          onError={error} 
        />
        {
          If(form.errors[ key ], () => (
            <FieldHelp error>{form.errors[ key ]}</FieldHelp>
          ))
            .ElseIf(slots.help, () => (
              <FieldHelp>{slots.help}</FieldHelp>
            ))
            .EndIf()
        }
      </Control>
    </Field>
  );
});
